import Oidc from "oidc-client";
import store from "../store/index";
import jwtDecode from "jwt-decode";
import { UserCustomNotificationType } from "@/lib/utilities/enums.js";

Oidc.Log.logger = console;
Oidc.Log.level = Oidc.Log.ERROR;

// Redirect URIs
const REDIRECT_URI = location.origin + "/callback.html";
const POST_LOGOUT_REDIRECT_URI = location.origin;
const SILENT_REDIRECT_URI = location.origin + "/silent.html";

function getUserStore() {
  // localStorage - If loaded via Pando desktop
  // sessionStorage - If loaded via browser
  let selectedStorage =
    window && window.Bridge ? window.localStorage : window.sessionStorage;
  return new Oidc.WebStorageStateStore({ store: selectedStorage });
}

var config = {
  authority: process.env.SSO_AUTHORITY,
  client_id: process.env.CLIENT,
  redirect_uri: REDIRECT_URI,
  response_type: "id_token token",
  scope: "openid profile email address roles all_claims aauser roles opp_api",
  userStore: getUserStore(),
  post_logout_redirect_uri: POST_LOGOUT_REDIRECT_URI,
  silent_redirect_uri: SILENT_REDIRECT_URI,
  automaticSilentRenew: false,
  accessTokenExpiringNotificationTime:
    store.getters.SessionExpirationModalDuration
};

export var webAuth = new Oidc.UserManager(config);

webAuth.user = {};
webAuth.user.isImpersonating = false;

webAuth.login = function () {
  return new Promise((resolve, reject) => {
    webAuth
      .getOAuthUser()
      .then(function (user) {
        resolve(user);
      })
      .catch(resp => {
        reject(resp);
      });
  });
};

webAuth.logout = async function (access_token) {
  try {
    const accessToken = jwtDecode(access_token);
    // Notify user
    await store.dispatch("sendCustomNotificationToUser", {
      UserId: store.getters.User.UserId,
      Payload: {
        action: UserCustomNotificationType.USER_PROFILE_LOGOUT,
        data: {
          sub: accessToken.sub,
          exp: accessToken.exp,
          nbf: accessToken.nbf,
          auth_time: accessToken.auth_time
        }
      }
    });
  } catch (err) {
    console.log("Error sending logout notification: ", err);
  }

  webAuth.signoutRedirect();
};

// If no valid user is found, redirect to SSO Login
webAuth.redirectToSigninPage = function () {
  // TODO: Handle network status event
  // The below comment code will enabled after network status event handlers are added
  // if (store.getters.IsOffline) {
  //   // If the user is offline, try again in 30 seconds
  //   setTimeout(() => {
  //     webAuth.redirectToSigninPage();
  //   }, 30 * 1000);
  //   return;
  // }

  if (window.logOAuth) console.log("----- User will now be redirected to signin page");
  window.sessionStorage.clear();
  window.localStorage.clear();
  sessionStorage.setItem("lastRoute", window.location);
  webAuth.signinRedirect();
};

webAuth.getOAuthUser = function () {
  return new Promise((resolve, reject) => {
    webAuth
      .getUser()
      .then(function (user) {
        if (webAuth.validateOAuthUser(user)) resolve(user);
        else
          webAuth.refreshAccessToken().then(resp => {
            resolve(resp);
          });
      })
      .catch(resp => {
        webAuth.refreshAccessToken().then(user => {
          resolve(user);
        });
      });
  });
};

webAuth.validateOAuthUser = function (user) {
  if (!user || user.expired) {
    return false;
  } else {
    // Check for user impersonation
    let idToken = jwtDecode(user.id_token);
    let accessToken = jwtDecode(user.access_token);
    webAuth.user.isImpersonating =
      (idToken.impersonation_id && idToken.impersonation_id.length > 0) ||
      (accessToken.impersonation_id && accessToken.impersonation_id.length > 0);
    store.dispatch("toggleIsImpersonatingUser", webAuth.user.isImpersonating);

    // Update OAuthUser in store, if the user session is valid
    store.dispatch("updateOAuthUser", user);
    return true;
  }
};

// Perform silent singin call to refresh the access token
webAuth.refreshAccessToken = function () {
  return new Promise((resolve, reject) => {
    webAuth
      .signinSilent()
      .then(user => {
        if (webAuth.validateOAuthUser(user)) resolve(user);
        else webAuth.displaySessionExpirationModal();
      })
      .catch(resp => {
        webAuth.displaySessionExpirationModal();
      });
  });
};

webAuth.impersonateUser = async function (username) {
  if (!username) {
    console.error("No username passed to impersonateUser, exiting...");
    return;
  }

  this.createSigninRequest({
    extraQueryParams: {
      acr_values: `impersonate_aa_username:${username}`,
      // This isn't needed in default condition, but if something breaks this will prompt the user for login instead of sending the user with the same user
      prompt: "login"
    }
  }).then(req => {
    location.href = req.url;
  });
};

webAuth.endImpersonation = async function () {
  this.createSigninRequest({
    extraQueryParams: {
      acr_values: `endImpersonation:true`,
      // This isn't needed in default condition, but if something breaks this will prompt the user for login instead of sending the user with the same user
      prompt: "login"
    }
  }).then(req => {
    location.href = req.url;
  });
};

webAuth.displaySessionExpirationModal = function () {
  // console.log("Session is about to expire");
  if (store.getters.AccessToken) {
    store.dispatch("toggleSessionExpirationModal", true);
    setTimeout(() => {
      webAuth.redirectToSigninPage();
    }, store.getters.SessionExpirationModalDuration * 1000);
  } else webAuth.redirectToSigninPage();
};

//#region ********** BEGIN USERMANAGER EVENTS' SUBSCRIPTIONS **********

// Raised when the user's access token is about to expire
webAuth.events.addAccessTokenExpiring(function () {
  if (window.logOAuth) console.log("----- User token is about to expire");
  if (webAuth.user.isImpersonating) webAuth.endImpersonation();
  else webAuth.refreshAccessToken();
});

// Raised when the user's access token is expired
webAuth.events.addAccessTokenExpired(function () {
  if (window.logOAuth) console.log("----- User token has expired");
  webAuth.refreshAccessToken();
});

// Raised when a user session has been established / re-established
webAuth.events.addUserLoaded(function (user) {
  if (window.logOAuth) console.log("----- User loaded");
  if (!webAuth.validateOAuthUser(user)) webAuth.refreshAccessToken();
});

// Raised when a user session has been terminated (logout)
webAuth.events.addUserUnloaded(function () {
  if (window.logOAuth) console.log("----- User unloaded");
  window.sessionStorage.clear();
  window.localStorage.clear();
});

// Raised when an automatic silent renew fails
webAuth.events.addSilentRenewError(function () {
  if (window.logOAuth) console.log("----- User silent renew error");
  webAuth.displaySessionExpirationModal();
});

// Raised when the user's signin status at the OpenID Provider has changed
webAuth.events.addUserSignedOut(function () {
  if (window.logOAuth) console.log("----- User signed out");
  webAuth.displaySessionExpirationModal();
});

//#endregion ********** END USERMANAGER EVENTS' SUBSCRIPTIONS **********



// WEBPACK FOOTER //
// ./src/auth/index.js